import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress, Alert, Card, CardContent, CardActions, Grid, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Accordion, AccordionSummary, AccordionDetails, Divider } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, ExpandMore as ExpandMoreIcon, Info as InfoIcon } from '@mui/icons-material';
import { InvestmentProfile } from '../../types/investment';
import { WatchlistManager } from './WatchlistManager';
//import { ScreenerManager } from './ScreenerManager';
import { EditProfileDialog } from './EditProfileDialog';
import { investmentAPI } from '../../api/investmentAPI';
import { useAuth } from '../../contexts/AuthContext';
import { useLoadingStatus } from '../../contexts/LoadingStatusContext';
import { useMemo } from 'react';

// Add the template types definition
interface ProfileTemplate {
  id: string;
  name: string;
  description: string;
  risk: string;
  potentialReturn: string;
  bestFor: string[];
}

interface RefType {
  refreshProfiles: () => void;
}

interface Props {
  onTickerSelect: (ticker: string) => void;
  onCreateClick: () => void;
  onProfilesChange?: () => void;
}

export const InvestmentProfileManager = React.forwardRef<RefType, Props>(({ 
  onTickerSelect,
  onCreateClick,
  onProfilesChange
}, ref) => {
  const [profiles, setProfiles] = useState<InvestmentProfile[] | null>(null);
  const [profileToEdit, setProfileToEdit] = useState<InvestmentProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<InvestmentProfile | null>(null);
  const [showTemplateInfo, setShowTemplateInfo] = useState(true);
  
  // Define available profile templates ordered by risk level (low to high)
  const profileTemplates: ProfileTemplate[] = [
    {
      id: "balanced",
      name: "Balanced Growth Investor",
      description: "Combines elements of multiple strategies to create a diversified approach to investing across different sectors and styles.",
      risk: "Medium",
      potentialReturn: "Medium",
      bestFor: ["Most investors", "Moderate risk tolerance", "Long-term financial planning"]
    },
    {
      id: "value",
      name: "Value Investor",
      description: "Seeks undervalued companies trading below their intrinsic value, often with low P/E ratios and higher dividend yields.",
      risk: "Medium-High",
      potentialReturn: "Medium-High",
      bestFor: ["Patient investors", "Medium risk tolerance", "Higher potential returns", "Long-term investment horizon"]
    },
    {
      id: "dividend",
      name: "Quality Income Investor",
      description: "Targets companies with stable cash flows that regularly distribute earnings to shareholders through dividends.",
      risk: "Low-Medium",
      potentialReturn: "Low-Medium",
      bestFor: ["Retirees", "Income-focused investors", "Lower risk tolerance"]
    },
    {
      id: "growth",
      name: "Aggressive Growth Investor",
      description: "Focuses on companies with high growth potential that are expected to grow sales and earnings faster than industry or market average.",
      risk: "High",
      potentialReturn: "High",
      bestFor: ["Medium-term investors", "Higher risk tolerance", "Seeking capital appreciation"]
    },
    {
      id: "momentum",
      name: "Ultra-Aggressive Active Trader",
      description: "Focuses on stocks expected to give big returns, buying rising stocks and avoiding falling ones.",
      risk: "High",
      potentialReturn: "High",
      bestFor: ["Active traders", "Technical analysis followers", "Higher risk tolerance", "Short-term trading"]
    }
  ];

  // Use auth context
  const { addStatus, removeStatus } = useLoadingStatus();
  const api = useMemo(() => investmentAPI(addStatus, removeStatus), []);

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const data = await api.getProfiles();
      console.log('Fetched profiles:', data);
      setProfiles(data);
      onProfilesChange?.();
    } catch (err) {
      console.error('Failed to fetch profiles:', err);
      setError('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (profile: InvestmentProfile) => {
    setProfileToDelete(profile);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!profileToDelete) return;
    
    try {
      setLoading(true);
      await api.deleteProfile(profileToDelete.id);
      setShowDeleteDialog(false);
      setProfileToDelete(null);
      await fetchProfiles();
      onProfilesChange?.();
    } catch (err) {
      setError('Failed to delete profile');
    } finally {
      setLoading(false);
    }
  };

  // Expose refreshProfiles to parent with guaranteed implementation
  React.useImperativeHandle(ref, () => ({
    refreshProfiles: () => {
      fetchProfiles();
    }
  }));

  useEffect(() => {
    fetchProfiles();
  }, []);

  const handleProfileUpdate = (updatedProfile: InvestmentProfile) => {
    console.log('Profile updated:', updatedProfile);
    fetchProfiles();
    onProfilesChange?.();
    setIsEditDialogOpen(false);
    setProfileToEdit(null);
  };

  return (
    <Box>
      {/* Your Investment Profiles Section */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" color="text.secondary">Your Investment Profiles</Typography>
        {!loading && profiles && profiles.length < 5 && (
          <Button
            variant="contained"
            color="primary"
            onClick={onCreateClick}
            startIcon={<AddIcon />}
          >
            Create Profile
          </Button>
        )}
      </Box>

      {profiles && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {profiles.length} of 5 profiles used
          </Typography>
          {profiles.length >= 5 && (
            <Typography 
              variant="body2" 
              color="warning.main"
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                ml: 2,
                backgroundColor: 'warning.light',
                px: 1,
                py: 0.5,
                borderRadius: 1
              }}
            >
              Maximum limit reached. Delete an existing profile to create a new one.
            </Typography>
          )}
        </Box>
      )}
      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {!loading && profiles && profiles.map((profile, index) => (
          <Grid item xs={12} sm={6} md={4} key={`profile-${profile.id}-${index}`}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6">
                    {profile.name}
                  </Typography>
                  <Box>
                    <IconButton 
                      size="small"
                      onClick={() => {
                        setProfileToEdit(profile);
                        setIsEditDialogOpen(true);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDeleteClick(profile)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Template: {profile.investment_profile_template}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Created: {new Date(profile.created_at).toLocaleDateString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Profile Template Information Section - Moved below user profiles */}
      <Accordion 
        expanded={showTemplateInfo} 
        onChange={() => setShowTemplateInfo(!showTemplateInfo)}
        sx={{ mb: 3, borderRadius: 1, overflow: 'hidden' }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{ backgroundColor: 'primary.light', color: 'primary.contrastText' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon />
            <Typography variant="subtitle1">Available Investment Profile Templates</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" paragraph>
            Learn about the different investment styles available when creating your profile. Each template offers a different strategy with varying risk and return profiles.
          </Typography>
          
          <Grid container spacing={2}>
            {profileTemplates.map((template) => (
              <Grid item xs={12} md={6} key={template.id}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                      {template.name}
                    </Typography>
                    <Typography variant="body2" paragraph>
                      {template.description}
                    </Typography>
                    
                    {/* Risk and Return Information */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Risk Level:
                        </Typography>
                        <Typography variant="body2" 
                          sx={{ 
                            fontWeight: 'medium',
                            color: template.risk === 'High' ? 'error.main' : 
                                   template.risk === 'Medium' ? 'warning.main' : 'success.main'
                          }}
                        >
                          {template.risk}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Potential Return:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {template.potentialReturn}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Typography variant="subtitle2" color="text.secondary">
                      Best for:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 0.5 }}>
                      {template.bestFor.map((item, index) => (
                        <Typography component="li" variant="body2" key={index} sx={{ mb: 0.5 }}>
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {profileToEdit && (
        <EditProfileDialog
          open={isEditDialogOpen}
          profile={profileToEdit}
          onClose={() => {
            setIsEditDialogOpen(false);
            setProfileToEdit(null);
          }}
          onSave={handleProfileUpdate}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete Profile</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the profile "{profileToDelete?.name}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});
